if (!customElements.get('product-discount-campaign')) {
  customElements.define(
    'product-discount-campaign',
    class ProductDiscountCampaign extends HTMLElement {
      static storageKey = 'ikier-active-discount-campaigns';

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.config = this.parseJson('[data-campaign-config]');
        this.translations = this.parseJson('[data-campaign-translations]');
        this.campaigns = Array.isArray(this.config) ? this.config : this.config.campaigns || [];
        this.currentVariantId = String(this.dataset.selectedVariantId || '');
        this.statuses = new Map();
        this.listenersBound = false;

        this.onHashChangeHandler = this.onHashChange.bind(this);
        window.addEventListener('hashchange', this.onHashChangeHandler);
        this.activate();
      }

      disconnectedCallback() {
        window.removeEventListener('hashchange', this.onHashChangeHandler);
        document.removeEventListener('variant:changed', this.onVariantChangedHandler);
        document.removeEventListener('product-ajax:added', this.onProductAddedHandler);
        this.removeEventListener('click', this.onClickHandler);
        this.cartUpdateUnsubscriber?.();
      }

      bindListeners() {
        if (this.listenersBound) return;
        this.listenersBound = true;
        this.onVariantChangedHandler = this.onVariantChanged.bind(this);
        this.onProductAddedHandler = this.onProductAdded.bind(this);
        this.onClickHandler = this.onClick.bind(this);
        document.addEventListener('variant:changed', this.onVariantChangedHandler);
        document.addEventListener('product-ajax:added', this.onProductAddedHandler);
        this.addEventListener('click', this.onClickHandler);
        this.cartUpdateUnsubscriber = window.FoxTheme?.pubsub?.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          this.onCartUpdate.bind(this)
        );
      }

      onHashChange() {
        this.activate();
      }

      activate() {
        this.activeCampaigns = this.getActiveCampaigns();
        if (!this.activeCampaigns.length) {
          this.hidden = true;
          return;
        }
        this.bindListeners();
        this.render();
        this.hydrateFromCart();
      }

      async hydrateFromCart() {
        try {
          const cart = await this.getCart();
          this.syncStatusesFromCart(cart);
          this.render();
        } catch (error) {
          console.error(error);
        }
      }

      parseJson(selector) {
        const element = this.querySelector(selector);
        if (!element) return {};
        try {
          return JSON.parse(element.textContent);
        } catch (error) {
          console.error('Invalid product discount campaign configuration.', error);
          return {};
        }
      }

      normalize(value) {
        return String(value || '')
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '');
      }

      unwrapType(value) {
        let raw = value;
        if (Array.isArray(raw)) raw = raw[0];
        if (raw && typeof raw === 'object') {
          raw = raw.value || raw.handle || raw.label || '';
        }
        return this.normalize(raw);
      }

      campaignType(campaign) {
        return this.unwrapType(campaign?.type);
      }

      isGiftCampaign(campaign) {
        const type = this.campaignType(campaign);
        if (['buyxgety', 'bxgy', 'gift', 'freegift', 'freeproduct'].includes(type)) return true;
        return Boolean(campaign?.reward_variant_id);
      }

      campaignMatchesToken(campaign, token) {
        const normalized = this.normalize(token);
        if (!normalized) return false;
        return [campaign.key, campaign.code, campaign.type].some(
          (value) => this.normalize(Array.isArray(value) ? value[0] : value) === normalized
        );
      }

      readTokensFromParams(params) {
        return params
          .getAll('campaign')
          .flatMap((value) => value.split(','))
          .map((value) => value.trim())
          .filter(Boolean);
      }

      readCampaignTokens() {
        const hash = window.location.hash.replace(/^#/, '');
        const hashTokens = this.readTokensFromParams(new URLSearchParams(hash));
        const searchTokens = this.readTokensFromParams(new URLSearchParams(window.location.search));
        return [...hashTokens, ...searchTokens];
      }

      getActiveCampaigns() {
        const requested = this.readCampaignTokens();
        let tokens = requested;

        try {
          if (requested.length) {
            sessionStorage.setItem(ProductDiscountCampaign.storageKey, JSON.stringify(requested));
          } else {
            tokens = JSON.parse(sessionStorage.getItem(ProductDiscountCampaign.storageKey) || '[]');
          }
        } catch (error) {
          tokens = requested;
        }

        return this.campaigns.filter((campaign) =>
          tokens.some((token) => this.campaignMatchesToken(campaign, token))
        );
      }

      onClick(event) {
        const button = event.target.closest('[data-claim-offer]');
        if (!button || button.disabled || this.processing) return;
        const campaign = this.findCampaign(button.dataset.claimOffer);
        if (campaign) this.claimOffer(campaign);
      }

      findCampaign(token) {
        const normalized = this.normalize(token);
        return this.activeCampaigns.find(
          (campaign) =>
            this.normalize(campaign.key) === normalized || this.normalize(campaign.code) === normalized
        );
      }

      onVariantChanged(event) {
        const variantId = event.detail?.variant?.id;
        if (!variantId) return;
        this.currentVariantId = String(variantId);
        this.render();
      }

      async onProductAdded(event) {
        if (this.processing) return;

        const addedVariantId = String(event.detail?.product?.variant_id || event.detail?.product?.id || '');
        const relevantCampaigns = this.activeCampaigns.filter((campaign) =>
          this.isEligible(campaign, addedVariantId)
        );
        if (!relevantCampaigns.length) return;

        this.processing = true;
        relevantCampaigns
          .filter((campaign) => this.isGiftCampaign(campaign))
          .forEach((campaign) => this.statuses.set(this.normalize(campaign.code), 'adding'));
        this.render();

        try {
          for (const campaign of relevantCampaigns) {
            if (this.isGiftCampaign(campaign)) await this.addRewardIfMissing(campaign);
          }
          const state = await this.applyDiscountCodes(relevantCampaigns);
          this.updateStatusesFromDiscountState(state, relevantCampaigns);
          document.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } })
          );
        } catch (error) {
          console.error(error);
          relevantCampaigns.forEach((campaign) => {
            const status = this.isGiftCampaign(campaign) ? 'gift-error' : 'invalid';
            this.statuses.set(this.normalize(campaign.code), status);
          });
        } finally {
          this.processing = false;
          this.render();
        }
      }

      async claimOffer(campaign) {
        if (this.processing || !this.isEligible(campaign) || !this.isCurrentVariantAvailable()) return;

        this.processing = true;
        this.statuses.set(this.normalize(campaign.code), 'adding');
        this.render();

        try {
          await this.addItems([
            { id: Number(this.getSelectedVariantId()), quantity: this.getQuantity() },
          ]);

          if (this.isGiftCampaign(campaign)) {
            try {
              await this.addRewardIfMissing(campaign);
            } catch (giftError) {
              console.error(giftError);
              this.statuses.set(this.normalize(campaign.code), 'gift-error');
              document.dispatchEvent(
                new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } })
              );
              return;
            }
          }

          const state = await this.applyDiscountCodes([campaign]);
          this.updateStatusesFromDiscountState(state, [campaign]);
          document.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } })
          );
        } catch (error) {
          console.error(error);
          this.statuses.set(this.normalize(campaign.code), 'invalid');
        } finally {
          this.processing = false;
          this.render();
        }
      }

      onCartUpdate(payload = {}) {
        if (this.processing) return;
        const cart = payload.cart || payload;
        if (!cart || (!Array.isArray(cart.items) && !Array.isArray(cart.discount_codes))) return;
        this.syncStatusesFromCart(cart);
        this.render();
      }

      isEligible(campaign, variantId = this.getSelectedVariantId() || this.currentVariantId) {
        const productIds = (campaign.eligible_product_ids || []).map(String);
        const variantIds = (campaign.eligible_variant_ids || []).map(String);
        if (productIds.length && !productIds.includes(String(this.dataset.productId))) return false;
        if (variantIds.length && !variantIds.includes(String(variantId))) return false;
        return true;
      }

      isCurrentVariantAvailable() {
        const button = this.closest('product-info')?.querySelector('.product-form__submit');
        if (!button) return Boolean(this.getSelectedVariantId());
        return !button.hasAttribute('disabled') && button.getAttribute('aria-disabled') !== 'true';
      }

      getSelectedVariantId() {
        const input = this.closest('product-info')?.querySelector('form[is="product-form"] [name="id"]');
        return String(input?.value || this.currentVariantId || '');
      }

      getQuantity() {
        const input = this.closest('product-info')?.querySelector('quantity-input input[name="quantity"]');
        const quantity = Number(input?.value || 1);
        return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      }

      async getCart() {
        const response = await fetch(
          FoxTheme.routes.cart_url,
          FoxTheme.utils.fetchConfig('json', 'GET')
        );
        if (!response.ok) throw new Error('Unable to load the cart.');
        return response.json();
      }

      async addItems(items) {
        const response = await fetch(FoxTheme.routes.cart_add_url, {
          ...FoxTheme.utils.fetchConfig('javascript'),
          body: JSON.stringify({ items }),
        });
        const state = await response.json();
        if (!response.ok || state.status) {
          throw new Error(state.description || 'Unable to add the campaign items.');
        }
        return state;
      }

      async addRewardIfMissing(campaign) {
        const rewardVariantId = String(campaign.reward_variant_id || '');
        if (!rewardVariantId) throw new Error('The reward variant is missing.');

        const cart = await this.getCart();
        if (cart.items?.some((item) => String(item.variant_id) === rewardVariantId)) return;

        await this.addItems([
          { id: Number(rewardVariantId), quantity: Number(campaign.reward_quantity || 1) },
        ]);
      }

      async setCartDiscounts(codes) {
        const formData = new FormData();
        formData.append('discount', codes);
        const config = FoxTheme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const response = await fetch(FoxTheme.routes.cart_update_url, config);
        const state = await response.json();
        if (!response.ok || state.status) {
          throw new Error(state.description || 'Unable to apply the discount campaign.');
        }
        return state;
      }

      async applyDiscountCodes(campaigns) {
        const cart = await this.getCart();
        const campaignCodes = campaigns.map((campaign) => campaign.code).filter(Boolean);
        const campaignKeys = new Set(campaignCodes.map((code) => this.normalize(code)));
        const existing = cart.discount_codes || [];
        const otherCodes = existing
          .filter(
            (discount) =>
              discount.applicable !== false && !campaignKeys.has(this.normalize(discount.code))
          )
          .map((discount) => discount.code)
          .filter(Boolean);
        const alreadyPresent = existing.some((discount) =>
          campaignKeys.has(this.normalize(discount.code))
        );

        if (alreadyPresent) {
          await this.setCartDiscounts(otherCodes.join(','));
        }

        return this.setCartDiscounts([...otherCodes, ...campaignCodes].join(','));
      }

      cartQualifies(campaign, cart) {
        const items = cart?.items || [];
        if (!items.length) return false;

        const variantIds = (campaign.eligible_variant_ids || []).map(String);
        const productIds = (campaign.eligible_product_ids || []).map(String);
        const hasEligible = items.some((item) => {
          if (variantIds.length) return variantIds.includes(String(item.variant_id));
          if (productIds.length) return productIds.includes(String(item.product_id));
          return String(item.product_id) === String(this.dataset.productId);
        });
        if (!hasEligible) return false;
        if (!this.isGiftCampaign(campaign)) return true;
        return items.some((item) => String(item.variant_id) === String(campaign.reward_variant_id));
      }

      syncStatusesFromCart(cart) {
        const discountCodes = cart?.discount_codes || [];
        this.activeCampaigns.forEach((campaign) => {
          const key = this.normalize(campaign.code);
          const discount = discountCodes.find(
            (item) => this.normalize(item.code) === this.normalize(campaign.code)
          );

          if (discount?.applicable === true) {
            this.statuses.set(key, 'applied');
            return;
          }

          if (!this.cartQualifies(campaign, cart)) {
            this.statuses.delete(key);
            return;
          }

          const current = this.statuses.get(key);
          if (current === 'invalid' || current === 'gift-error') return;
          this.statuses.delete(key);
        });
      }

      updateStatusesFromDiscountState(state, campaigns) {
        const discountCodes = state?.discount_codes || [];
        campaigns.forEach((campaign) => {
          const key = this.normalize(campaign.code);
          const discount = discountCodes.find(
            (item) => this.normalize(item.code) === this.normalize(campaign.code)
          );
          this.statuses.set(key, discount?.applicable === true ? 'applied' : 'invalid');
        });
      }

      formatMoney(amount, currency = 'EUR') {
        const money = amount && typeof amount === 'object' ? amount : { amount };
        const numericAmount = Number(money.amount || 0);
        const currencyCode = money.currency_code || money.currencyCode || currency;
        const locale = window.Shopify?.locale || document.documentElement.lang || 'en';
        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
          }).format(numericAmount);
        } catch (error) {
          return `${numericAmount} ${currencyCode}`;
        }
      }

      interpolate(template, replacements = {}) {
        return Object.entries(replacements).reduce(
          (output, [key, value]) => output.split(`__${key}__`).join(value),
          template || ''
        );
      }

      getStatusText(status) {
        if (status === 'applied') return this.translations.applied;
        if (status === 'invalid') return this.translations.invalid;
        if (status === 'gift-error') return this.translations.gift_error;
        return '';
      }

      el(tag, className, text) {
        const node = document.createElement(tag);
        if (className) node.className = className;
        if (text != null) node.textContent = text;
        return node;
      }

      createSpinner() {
        const wrap = document.createElement('div');
        wrap.className = 'loading__spinner hidden';
        wrap.innerHTML =
          '<svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle></svg>';
        return wrap;
      }

      createMediaIcon(kind) {
        const icon = this.el('span', 'product-discount-campaign__icon');
        icon.setAttribute('aria-hidden', 'true');
        if (kind === 'check') {
          icon.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5 9.5 17 19 7"/></svg>';
        } else if (kind === 'gift') {
          icon.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8"/><path d="M2 7h20v5H2z"/><path d="M12 7v14"/><path d="M12 7H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>';
        } else {
          icon.textContent = '%';
        }
        return icon;
      }

      createCard(campaign) {
        const eligible = this.isEligible(campaign);
        const amount = this.formatMoney(campaign.discount_amount, campaign.currency);
        const rewardValue = this.formatMoney(campaign.reward_value, campaign.currency);
        const rewardTitle = campaign.reward_title || '';
        const isGift = this.isGiftCampaign(campaign);
        const cartStatus = this.statuses.get(this.normalize(campaign.code));
        const status =
          cartStatus === 'applied'
            ? 'applied'
            : eligible
              ? cartStatus || 'pending'
              : 'ineligible';
        const isAdding = status === 'adding' || this.processing;

        const card = this.el('article', 'product-discount-campaign__card');
        card.dataset.state = status;
        card.classList.add(
          isGift ? 'product-discount-campaign__card--gift' : 'product-discount-campaign__card--save'
        );

        if (isGift) {
          const banner = this.el('div', 'product-discount-campaign__banner');
          const bannerLabel = this.el('span', 'product-discount-campaign__banner-label');
          bannerLabel.append(this.createMediaIcon('gift'), this.el('span', '', this.translations.gift_eyebrow));
          banner.append(bannerLabel);
          if (campaign.code) {
            banner.append(this.el('span', 'product-discount-campaign__code', campaign.code));
          }
          card.append(banner);
        }

        const body = this.el('div', 'product-discount-campaign__body');

        const media = this.el('div', 'product-discount-campaign__media');
        const mediaImage = isGift ? campaign.reward_image : campaign.product_image;
        if (mediaImage) {
          const image = document.createElement('img');
          image.src = mediaImage;
          image.alt = isGift ? rewardTitle : '';
          image.width = 160;
          image.height = 160;
          image.loading = 'lazy';
          media.append(image);
        } else {
          media.append(
            this.createMediaIcon(status === 'applied' ? 'check' : isGift ? 'gift' : 'percent')
          );
        }

        const content = this.el('div', 'product-discount-campaign__content');

        if (!isGift) {
          const top = this.el('div', 'product-discount-campaign__top');
          top.append(
            this.el('p', 'product-discount-campaign__eyebrow', this.translations.save_eyebrow),
            this.el('span', 'product-discount-campaign__code', campaign.code)
          );
          content.append(top);
        }

        const title = this.el(
          'p',
          'product-discount-campaign__title',
          isGift ? rewardTitle : this.interpolate(this.translations.single_title, { AMOUNT: amount })
        );

        content.append(title);

        if (isGift && rewardValue) {
          const value = this.el('p', 'product-discount-campaign__value');
          value.append(
            this.el('s', '', rewardValue),
            this.el('span', 'product-discount-campaign__free', this.translations.free_label)
          );
          content.append(value);
        }

        const description = this.el('p', 'product-discount-campaign__description');
        if (!eligible) {
          description.textContent = isGift
            ? this.translations.gift_ineligible
            : this.interpolate(this.translations.single_description, { AMOUNT: amount });
        } else if (isGift) {
          description.textContent = this.interpolate(this.translations.gift_description, {
            REWARD: rewardTitle,
            VALUE: rewardValue,
          });
        } else {
          description.textContent = this.interpolate(this.translations.single_description, {
            AMOUNT: amount,
          });
        }
        content.append(description);

        const statusText = this.getStatusText(status);
        if (statusText) {
          content.append(this.el('p', 'product-discount-campaign__status', statusText));
        }

        if (eligible && status !== 'applied') {
          const actions = this.el('div', 'product-discount-campaign__actions');
          const button = this.el('button', 'product-discount-campaign__claim btn btn--primary');
          button.type = 'button';
          button.dataset.claimOffer = campaign.key || campaign.code;

          const label = this.el('span');
          if (isAdding) {
            label.textContent = this.translations.claiming;
            button.classList.add('btn--loading');
            button.disabled = true;
            button.setAttribute('aria-busy', 'true');
          } else if (!this.isCurrentVariantAvailable()) {
            label.textContent = this.translations.sold_out;
            button.disabled = true;
          } else {
            label.textContent = isGift ? this.translations.claim_gift : this.translations.claim;
          }

          button.append(label);
          if (!isAdding && this.isCurrentVariantAvailable()) {
            const arrow = this.el('span', 'product-discount-campaign__claim-arrow');
            arrow.setAttribute('aria-hidden', 'true');
            arrow.innerHTML =
              '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8h10M9 4l4 4-4 4"/></svg>';
            button.append(arrow);
          }
          button.append(this.createSpinner());
          actions.append(button);
          content.append(actions);
        }

        body.append(media, content);
        card.append(body);
        return card;
      }

      render() {
        const list = this.querySelector('[data-campaign-list]');
        if (!list || !this.activeCampaigns.length) return;
        list.replaceChildren(...this.activeCampaigns.map((campaign) => this.createCard(campaign)));
        this.hidden = false;
        this.toggleDynamicCheckout();
      }

      toggleDynamicCheckout() {
        const shouldHide = this.activeCampaigns.some(
          (campaign) => this.isGiftCampaign(campaign) && this.isEligible(campaign)
        );
        this.closest('product-info')
          ?.querySelectorAll('.shopify-payment-button')
          .forEach((button) => {
            if (shouldHide) {
              button.dataset.hiddenByDiscountCampaign = 'true';
              button.hidden = true;
            } else if (button.dataset.hiddenByDiscountCampaign === 'true') {
              button.hidden = false;
              delete button.dataset.hiddenByDiscountCampaign;
            }
          });
      }
    }
  );
}
